import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { loginUser, registerServiceUser, getCurrentUser, logoutUser, deleteUser, getProviderProfile, getUserProfile } from '../services/api';
import { User, LoginFormData, RegisterServiceUserFormData } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (formData: LoginFormData) => Promise<{ success: boolean; error?: string }>;
  registerUser: (formData: RegisterServiceUserFormData) => Promise<{ success: boolean; error?: string; message?: string }>;
  logout: () => Promise<void>;
  deleteAccount: (reason: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
  updateProviderProfileStatus: (hasProfile: boolean) => void;
  updateUserProfileStatus: (hasProfile: boolean, isCompleted: boolean) => void;
  refreshUserProfileStatus: () => Promise<void>;
  adminLogin?: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<{
    user: User | null;
    loading: boolean;
    error: string | null;
    isAuthenticated: boolean;
  }>({
    user: null,
    loading: true,
    error: null,
    isAuthenticated: false,
  });

  const checkProviderProfile = async (userId: string): Promise<boolean> => {
    try {
      console.log('[AuthContext] Checking provider profile for userId:', userId);
      const profileResponse = await getProviderProfile(userId);

      if (!profileResponse.success || !profileResponse.provider) {
        console.log('[AuthContext] Profile not found or request failed');
        return false;
      }

      const profile = profileResponse.provider;
      console.log('[AuthContext] Profile data:', {
        typeOfCare: profile.typeOfCare,
        address: profile.address,
        city: profile.city,
        services: profile.services,
        serviceRadiusKm: profile.serviceRadiusKm,
        pricing: profile.pricing
      });

      const typeOfCareArray = Array.isArray(profile.typeOfCare)
        ? profile.typeOfCare
        : (typeof profile.typeOfCare === 'string' && profile.typeOfCare.trim() !== '')
          ? [profile.typeOfCare]
          : [];

      const hasTypeOfCare = typeOfCareArray.length > 0;
      const hasLocation = Boolean(
        (profile.address && profile.address.trim() !== '') ||
        (profile.city && profile.city.trim() !== '')
      );
      const hasServices = Array.isArray(profile.services) && profile.services.length > 0;
      const hasServiceRadius = Boolean(profile.serviceRadiusKm && profile.serviceRadiusKm > 0);

      const hasPricing = Boolean(
        profile.pricing &&
        profile.pricing.hourly &&
        profile.pricing.hourly > 0
      );

      console.log('[AuthContext] Profile validation:', {
        hasTypeOfCare,
        hasLocation,
        hasServices,
        hasServiceRadius,
        hasPricing,
        isComplete: hasTypeOfCare && hasLocation && hasServices && hasServiceRadius && hasPricing
      });

      return hasTypeOfCare && hasLocation && hasServices && hasServiceRadius && hasPricing;
    } catch (error) {
      console.error('[AuthContext] Error checking provider profile:', error);
      return false;
    }
  };

  const checkUserProfile = async (): Promise<{ hasProfile: boolean; isCompleted: boolean }> => {
    try {
      const profileResponse = await getUserProfile();

      if (!profileResponse.success || !profileResponse.profile) {
        return { hasProfile: false, isCompleted: false };
      }

      const profile = profileResponse.profile;

      return {
        hasProfile: true,
        isCompleted: profile.isProfileCompleted === true
      };
    } catch (error) {
      return { hasProfile: false, isCompleted: false };
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await getCurrentUser();
        if (response.success && response.data) {
          let userData = response.data;

          if (userData.role === 'admin') {
            setAuthState({
              user: userData,
              loading: false,
              error: null,
              isAuthenticated: true,
            });
          } else if (userData.role === 'provider' && userData.userId) {
            const hasProfile = await checkProviderProfile(userData.userId);
            userData = { ...userData, hasProviderProfile: hasProfile };
            localStorage.setItem('userData', JSON.stringify(userData));

            setAuthState({
              user: userData,
              loading: false,
              error: null,
              isAuthenticated: true,
            });
          } else if (userData.role === 'user' && userData.userId) {
            const profileStatus = await checkUserProfile();
            userData = {
              ...userData,
              hasUserProfile: profileStatus.hasProfile,
              isProfileCompleted: profileStatus.isCompleted
            };
            localStorage.setItem('userData', JSON.stringify(userData));

            setAuthState({
              user: userData,
              loading: false,
              error: null,
              isAuthenticated: true,
            });
          } else {
            setAuthState({
              user: userData,
              loading: false,
              error: null,
              isAuthenticated: true,
            });
          }
        } else {
          setAuthState({
            user: null,
            loading: false,
            error: null,
            isAuthenticated: false,
          });
        }
      } else {
        setAuthState({
          user: null,
          loading: false,
          error: null,
          isAuthenticated: false,
        });
      }
    };

    initAuth();
  }, []);

  const login = async (formData: LoginFormData) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));

    const response = await loginUser(formData);

    if (response.success && response.data) {
      let userData = response.data.user;

      if (userData.role === 'admin') {
        setAuthState({
          user: userData,
          loading: false,
          error: null,
          isAuthenticated: true,
        });
        return { success: true };
      } else if (userData.role === 'provider' && userData.userId) {
        const hasProfile = await checkProviderProfile(userData.userId);
        userData = { ...userData, hasProviderProfile: hasProfile };

        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const parsedData = JSON.parse(storedUserData);
          localStorage.setItem('userData', JSON.stringify({ ...parsedData, hasProviderProfile: hasProfile }));
        }
      } else if (userData.role === 'user' && userData.userId) {
        const profileStatus = await checkUserProfile();
        userData = {
          ...userData,
          hasUserProfile: profileStatus.hasProfile,
          isProfileCompleted: profileStatus.isCompleted
        };

        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const parsedData = JSON.parse(storedUserData);
          localStorage.setItem('userData', JSON.stringify({
            ...parsedData,
            hasUserProfile: profileStatus.hasProfile,
            isProfileCompleted: profileStatus.isCompleted
          }));
        }
      }

      setAuthState({
        user: userData,
        loading: false,
        error: null,
        isAuthenticated: true,
      });
      return { success: true };
    } else {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: response.error || 'Login failed',
      }));
      return { success: false, error: response.error };
    }
  };

  const registerUser = async (formData: RegisterServiceUserFormData) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));

    const response = await registerServiceUser(formData);

    setAuthState(prev => ({ ...prev, loading: false }));

    if (response.success) {
      return { success: true, message: response.message };
    } else {
      setAuthState(prev => ({
        ...prev,
        error: response.error || 'Registration failed',
      }));
      return { success: false, error: response.error };
    }
  };


  const logout = async () => {
    await logoutUser();
    setAuthState({
      user: null,
      loading: false,
      error: null,
      isAuthenticated: false,
    });
  };

  const deleteAccount = async (reason: string) => {
    if (!authState.user?.userId) {
      return { success: false, error: 'User ID not found' };
    }

    if (!authState.user?.role) {
      return { success: false, error: 'User role not found' };
    }

    if (!reason || reason.trim().length < 10) {
      return { success: false, error: 'Valid reason is required (minimum 10 characters)' };
    }

    const response = await deleteUser(authState.user.userId, authState.user.role, reason);

    if (response.success) {
      return { success: true };
    } else {
      return { success: false, error: response.error };
    }
  };

  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  const updateProviderProfileStatus = (hasProfile: boolean) => {
    setAuthState(prev => {
      const updatedUser = prev.user ? { ...prev.user, hasProviderProfile: hasProfile } : null;

      if (updatedUser) {
        localStorage.setItem('userData', JSON.stringify(updatedUser));
      }

      return {
        ...prev,
        user: updatedUser,
      };
    });
  };

  const updateUserProfileStatus = (hasProfile: boolean, isCompleted: boolean) => {
    setAuthState(prev => {
      const updatedUser = prev.user ? {
        ...prev.user,
        hasUserProfile: hasProfile,
        isProfileCompleted: isCompleted
      } : null;

      if (updatedUser) {
        localStorage.setItem('userData', JSON.stringify(updatedUser));
      }

      return {
        ...prev,
        user: updatedUser,
      };
    });
  };

  const refreshUserProfileStatus = async () => {
    if (!authState.user || authState.user.role !== 'user') {
      return;
    }

    const profileStatus = await checkUserProfile();

    setAuthState(prev => {
      const updatedUser = prev.user ? {
        ...prev.user,
        hasUserProfile: profileStatus.hasProfile,
        isProfileCompleted: profileStatus.isCompleted
      } : null;

      if (updatedUser) {
        localStorage.setItem('userData', JSON.stringify(updatedUser));
      }

      return {
        ...prev,
        user: updatedUser,
      };
    });
  };

  const adminLogin = async () => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setAuthState({
        user: parsedUser,
        loading: false,
        error: null,
        isAuthenticated: true,
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        registerUser,
        logout,
        deleteAccount,
        clearError,
        updateProviderProfileStatus,
        updateUserProfileStatus,
        refreshUserProfileStatus,
        adminLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
